import React from "react";
import { TFarmDataResponse } from "../../../services/farm.service";
import { Insights, Sensors, TestIds } from "..";
import SpeedoMeter from "./SpeedoMeter";
import { useRecoilValue } from "recoil";
import { languageAtom } from "../../../state/atoms/language.atom";
import { t } from "../../../services/locale.service";

const SpeedoMetersContainer: React.FC<TProps> = (props) => {
  const { farmData, plotId, speedoMeterFor } = props;
  const SpeedoMeterSize = Math.min((getWindowDimensions()?.width || 400) / 2.8, 140);

  // Safety check for farmData
  if (!farmData || typeof farmData !== "object") {
    console.warn("SpeedoMetersContainer: Invalid farmData provided", farmData);
    return null;
  }

  // Safety check for plotId
  if (!plotId || typeof plotId !== "string") {
    console.warn("SpeedoMetersContainer: Invalid plotId provided", plotId);
    return null;
  }

  const selectedLanguage = useRecoilValue(languageAtom) || "en";
  const TitleMapping = {
    sensor: t("FarmData") || "Farm Data",
    disease: t("Disease") || "Disease",
    pest: t("Pest") || "Pest",
    insight: t("Insights") || "Insights"
  };

  const title = TitleMapping[speedoMeterFor || "sensor"] || "Sensors";

  const testId = (TestIds?.speedoMeterContainer && typeof TestIds.speedoMeterContainer === "string") 
    ? TestIds.speedoMeterContainer.replace(":speedoMeterFor", speedoMeterFor || "unknown")
    : "speedo-meter-container";


  const getSpeedoMeterData = (sensor: string) => {
    // Safety check to ensure sensor data exists
    if (!farmData[sensor]) {
      return {
        highlights: [],
        value: 0,
        majorTicks: ["0", "100"],
        activeLabel: null,
        description: "N/A",
        descriptionBackgroundColor: "#ccc",
        isPestNotApplicable: false,
      };
    }

    const highlights = farmData[sensor].labels?.map((label) => ({
      from: label.min,
      to: label.max,
      color: label.activeBarColor,
    })) || [];

    const value = Number(farmData[sensor]?.value?.toFixed(2));
    const majorTicks = [
      (farmData[sensor].minValue ?? 0).toString(),
      (farmData[sensor].maxValue ?? 100).toString(),
    ];

    const activeLabel = farmData[sensor].labels?.filter((label) => {
      return label.min <= value && label.max >= value;
    })[0];

    const description = activeLabel?.name || "N/A";
    const descriptionBackgroundColor = activeLabel?.activeBarColor || "#ccc";

    // Check if this is a pest or disease and if it's not applicable
    const isPestNotApplicable = (speedoMeterFor === "pest" || speedoMeterFor === "disease") && farmData[sensor].isApplicable === false;

    return {
      highlights,
      value,
      majorTicks,
      activeLabel,
      description,
      descriptionBackgroundColor,
      isPestNotApplicable, // Add this flag for grayscale rendering
    };
  };

  if(!farmData || Object.keys(farmData).length === 0) {
    return null;
  }

  return (
    <div>
      <div style={TitleStyle}> {title} </div>
      <div data-testid={testId} style={SpeedoMeterContainerStyle}>
        {Object.keys(farmData).map((sensor, index) => {
          // Safety check to ensure sensor data exists
          if (!farmData[sensor]) {
            return null;
          }

          const SpeedoMeterSensors = speedoMeterFor === "sensor" ? Sensors : Insights;

          const isDisease = ["disease", "pest"].includes(speedoMeterFor!);
          if ((SpeedoMeterSensors.includes(sensor) || isDisease) && farmData[sensor]?.value) {
            const {
              highlights,
              value,
              majorTicks,
              description,
              descriptionBackgroundColor,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              isPestNotApplicable,
            } = getSpeedoMeterData(sensor);

            /**
             * TODO: sensor name color se aa raha
             */
            const sensorName = isDisease
              ? farmData[sensor].color
              : speedoMeterFor === "sensor"
                ? SensorKeyMapping[sensor]
                : InsightKeyMapping[sensor];

            const unit = isDisease ? "%" 
              : speedoMeterFor === "sensor" ? SensorUnits[sensor] : "";

            return (
              <SpeedoMeter
                key={`speedo-meter-${plotId}-${sensor}-${index}`}
                plotId={plotId}
                sensor={sensor}
                highlights={highlights}
                value={value}
                majorTicks={majorTicks}
                valueStatus={description}
                valueStatusColor={descriptionBackgroundColor}
                speedoMeterSize={SpeedoMeterSize}
                sensorName={sensorName}
                unit={unit}
                language={selectedLanguage ?? "en"}
                isDisease={speedoMeterFor === "disease"}
                isPest={speedoMeterFor === "pest"}
                isApplicable={
                  ["pest", "disease"].includes(speedoMeterFor!)
                    ? farmData[sensor].isApplicable
                    : true
                }
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default SpeedoMetersContainer;

/**
 * ================== TYPES ======================
 */

type TSpeedoMeterFor = "sensor" | "disease" | "pest" | "insight";

type TProps = {
  farmData: TFarmDataResponse;
  plotId: string;
  speedoMeterFor?: TSpeedoMeterFor;
};

/**
 * ================== Constants ======================
 */

const SensorKeyMapping: { [key: string]: string } = {
  airHumidity: "Air Humidity",
  airPressure: "Air Pressure",
  airTemp: "Air Temperature",
  leafWetness: "Leaf Wetness",
  lightIntensity: "Light Intensity",
  rainFall: "Rainfall",
  soilMoisture1: "Soil Moisture 1",
  soilMoisture2: "Soil Moisture 2",
  moisture1: "Moisture 1",
  moisture2: "Moisture 2",
  soilTemp: "Soil Temperature",
  windSpeed: "Wind Speed",
};

const SensorUnits: { [key: string]: string } = {
  soilMoisture1: "kPa",
  soilMoisture2: "kPa",
  moisture1: "%",
  moisture2: "%",
  airTemp: "℃",
  soilTemp: "℃",
  airHumidity: "%",
  rainFall: "mm",
  lightIntensity: "lux",
  airPressure: "kPa",
  windSpeed: "km/h",
};

const InsightKeyMapping: { [key: string]: string } = {
  deltaT: "Delta T",
  VPD: "VPD",
};

// styles
const SpeedoMeterContainerStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
};

const TitleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: "500",
  marginLeft: 15,
  marginTop: 15,
};

/**
 * ================== Helpers Functions ======================
 */

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};


// this file is also in use 